import { format } from '@if/core/date'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  ToggleButton,
  Typography,
} from '@mui/material'
import {
  useDocument,
  useLinkDocumentToCreditApp,
  useReplaceDocumentLink,
  useSetDiscardStatus,
  useUnlinkDocsAutomation,
} from '@src/api/document-api'
import ErrorAlert from '@src/components/Common/Error/ErrorAlert'
import { useNotification } from '@src/providers/NotificationProvider'
import { CreditAppMatchResult, DocsAutomation, EProcessingStatus } from '@src/types/automationDocument'
import { AxiosError } from 'axios'
import { createRef, Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Informations from './Informations'
import ManualLinkForm from './ManualLinkForm'
import Match from './Match'
import UnlinkConfirmDialog from './UnlinkConfirmDialog'

interface MatchDialogProps {
  docsAutomation: DocsAutomation | null
  onClose: () => void
}

const MatchDialog = ({ docsAutomation, onClose }: MatchDialogProps) => {
  const { t } = useTranslation()
  const { mutateAsync: linkDocumentToCreditApp, isPending, error: errorLink, reset } = useLinkDocumentToCreditApp()
  const { mutateAsync: replaceDocumentLink, isPending: isReplacing } = useReplaceDocumentLink()
  const { mutateAsync: setDiscardStatus, isPending: isUnDiscarding } = useSetDiscardStatus()
  const { showSuccess } = useNotification()
  const [expanded, setExpanded] = useState<boolean>(true)
  const [showManualLink, setShowManualLink] = useState<boolean>(false)
  const [selectedMatch, setSelectedMatch] = useState<CreditAppMatchResult | null>(null)
  const [multipleMatches, setMultipleMatches] = useState<CreditAppMatchResult[] | null>(null)
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState<boolean>(false)
  const [wantsReplace, setWantsReplace] = useState<boolean>(false)

  const cardRef = createRef<HTMLDivElement>()

  const extractedRefs = docsAutomation?.extractedReferenceNumbers ?? []
  const autofillRef = extractedRefs.length === 1 ? extractedRefs[0] : ''

  const {
    control: manualLinkControl,
    watch,
    reset: resetManualLink,
  } = useForm({
    defaultValues: {
      applicationNumber: autofillRef,
      numberType: 'dcc' as 'dcc' | 'cvt',
      financingType: 'fa' as 'fa' | 'ifx' | 'if',
    },
  })

  useEffect(() => {
    resetManualLink({
      applicationNumber: autofillRef,
      numberType: 'dcc',
      financingType: 'fa',
    })
  }, [autofillRef, resetManualLink])

  const {
    control: replaceFormControl,
    watch: watchReplace,
    reset: resetReplaceForm,
  } = useForm({
    defaultValues: {
      applicationNumber: '',
      numberType: 'dcc' as 'dcc' | 'cvt',
      financingType: 'fa' as 'fa' | 'ifx' | 'if',
    },
  })

  const numberTypeOptions = useMemo(
    () =>
      (['dcc', 'cvt'] as const).map((value) => (
        <ToggleButton key={value} value={value}>
          {value.toUpperCase()}
        </ToggleButton>
      )),
    [],
  )

  const financingTypeOptions = useMemo(
    () =>
      (['fa', 'ifx', 'if'] as const).map((value) => (
        <ToggleButton key={value} value={value}>
          {value.toUpperCase()}
        </ToggleButton>
      )),
    [],
  )

  const applicationNumber = watch('applicationNumber')
  const numberType = watch('numberType')
  const financingType = watch('financingType')

  const replaceApplicationNumber = watchReplace('applicationNumber')
  const replaceNumberType = watchReplace('numberType')
  const replaceFinancingType = watchReplace('financingType')

  const financingProgramMap = useMemo(
    () => ({
      fa: 0,
      ifx: 1,
      if: 2,
    }),
    [],
  )

  const { mutateAsync: unlinkDocsAutomation, isPending: isUnlinking } = useUnlinkDocsAutomation()
  const [documentWithPages] = useDocument(docsAutomation?.id)

  const hasManualDocumentTypes = useMemo(() => {
    return documentWithPages?.documentPages.some((page) => page.documentTypeId !== null) ?? false
  }, [documentWithPages])

  const handleClose = useCallback(() => {
    if (isPending || isUnDiscarding) return
    onClose()
    reset()
    setShowManualLink(false)
    setSelectedMatch(null)
    setMultipleMatches(null)
    resetManualLink()
    resetReplaceForm()
    setWantsReplace(false)
  }, [isPending, isUnDiscarding, onClose, reset, resetManualLink, resetReplaceForm])

  const handleMatch = useCallback(
    (possibleMatch: CreditAppMatchResult) => {
      setSelectedMatch((prev) => {
        // If clicking on the same match, deselect it
        if (prev?.id === possibleMatch.id) return null

        // If manual form is open, close it and reset
        if (showManualLink) {
          setShowManualLink(false)
          resetManualLink()
        }

        return possibleMatch
      })
    },
    [showManualLink, resetManualLink],
  )

  const isDiscarded = docsAutomation?.processingStatus === EProcessingStatus.Discarded
  const isFinalized = docsAutomation?.processingStatus === EProcessingStatus.Finalized

  const possibleMatchesResults = useMemo(() => {
    if (isDiscarded) return null

    if (docsAutomation?.linkedCreditApp) {
      return (
        <Match
          key={docsAutomation.linkedCreditApp.id}
          possibleMatch={docsAutomation.linkedCreditApp}
          variant="outlined"
          onSelect={handleMatch}
          disabled={true}
        />
      )
    }

    if (!docsAutomation?.systemMatchingResult) return null

    return docsAutomation.systemMatchingResult.map((match) => (
      <Match
        key={match.id}
        possibleMatch={match}
        variant="outlined"
        onSelect={handleMatch}
        disabled={isPending}
        selected={selectedMatch?.id === match.id}
      />
    ))
  }, [docsAutomation, handleMatch, isPending, selectedMatch, isDiscarded])

  const handleExpandedChange = useCallback(() => {
    setExpanded((prev) => !prev)
  }, [])

  const handleScroll = useCallback(() => {
    if (cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      cardRef.current.focus()
    }
  }, [cardRef])

  const notes = useMemo(() => {
    return (
      docsAutomation?.processingNotes
        .sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime())
        .map((note, index) => (
          <Fragment key={index}>
            {index > 0 && <Divider />}
            <ListItem>
              <ListItemText
                primary={
                  <>
                    {note.createdByUserFullName ? `${note.createdByUserFullName} - ` : ''}
                    {format(note.createdOn, 'P')}
                  </>
                }
                secondary={note.note}
              />
            </ListItem>
          </Fragment>
        )) ?? []
    )
  }, [docsAutomation?.processingNotes])

  const handleManualLinkClick = useCallback(() => {
    // If a match is selected, deselect it before opening manual form
    if (selectedMatch) {
      setSelectedMatch(null)
    }
    setShowManualLink(true)
  }, [selectedMatch])

  const handleSave = useCallback(() => {
    if (!docsAutomation) return

    // If a match is selected from the list
    if (selectedMatch) {
      void linkDocumentToCreditApp({
        documentId: docsAutomation.id,
        versionTag: docsAutomation.versionTag,
        creditApplicationId: selectedMatch.id,
        financingProgram: selectedMatch.productLine,
      }).then(() => {
        onClose()
        showSuccess()
      })
      return
    }

    // If manual link form is filled
    if (applicationNumber) {
      const linkParams = {
        documentId: docsAutomation.id,
        versionTag: docsAutomation.versionTag,
        financingProgram: financingProgramMap[financingType],
        ...(numberType === 'dcc'
          ? { dccNumber: parseInt(applicationNumber) }
          : { cvtNumber: parseInt(applicationNumber) }),
      }

      void linkDocumentToCreditApp(linkParams)
        .then(() => {
          // Reset and close manual form if open
          if (showManualLink) {
            setShowManualLink(false)
            resetManualLink()
          }
          onClose()
          showSuccess()
        })
        .catch((error: AxiosError) => {
          if (error.response?.status === 422) {
            // Multiple matches found
            const matches = error.response.data as CreditAppMatchResult[]
            setMultipleMatches(matches)
          }
        })
      return
    }
  }, [
    docsAutomation,
    selectedMatch,
    applicationNumber,
    numberType,
    financingType,
    financingProgramMap,
    linkDocumentToCreditApp,
    onClose,
    showSuccess,
    showManualLink,
    resetManualLink,
  ])

  const handleUnlinkClick = useCallback(() => {
    if (!hasManualDocumentTypes) {
      // If no manual document types, unlink directly without confirmation
      if (!docsAutomation?.linkedCreditApp) return
      void unlinkDocsAutomation(docsAutomation.id).then(() => {
        onClose()
        showSuccess()
      })
      return
    }
    setShowUnlinkConfirm(true)
  }, [hasManualDocumentTypes, docsAutomation, unlinkDocsAutomation, onClose, showSuccess])

  const handleUnlinkCancel = useCallback(() => {
    setShowUnlinkConfirm(false)
    setWantsReplace(false)
    resetReplaceForm()
  }, [resetReplaceForm])

  const handleUnlinkConfirm = useCallback(() => {
    if (!docsAutomation?.linkedCreditApp) return

    // If replace is requested, link to new application
    if (wantsReplace && replaceApplicationNumber) {
      const linkParams = {
        documentId: docsAutomation.id,
        versionTag: docsAutomation.versionTag,
        financingProgram: financingProgramMap[replaceFinancingType],
        ...(replaceNumberType === 'dcc'
          ? { dccNumber: parseInt(replaceApplicationNumber) }
          : { cvtNumber: parseInt(replaceApplicationNumber) }),
      }

      void replaceDocumentLink(linkParams)
        .then(() => {
          setShowUnlinkConfirm(false)
          setWantsReplace(false)
          resetReplaceForm()
          onClose()
          showSuccess()
        })
        .catch((error: AxiosError) => {
          if (error.response?.status === 422) {
            // Multiple matches found
            const matches = error.response.data as CreditAppMatchResult[]
            setMultipleMatches(matches)
            setShowUnlinkConfirm(false)
            setWantsReplace(false)
            resetReplaceForm()
          }
        })
      return
    }

    // Otherwise, just unlink
    void unlinkDocsAutomation(docsAutomation.id).then(() => {
      setShowUnlinkConfirm(false)
      setWantsReplace(false)
      resetReplaceForm()
      onClose()
      showSuccess()
    })
  }, [
    docsAutomation,
    unlinkDocsAutomation,
    onClose,
    showSuccess,
    wantsReplace,
    replaceApplicationNumber,
    replaceNumberType,
    replaceFinancingType,
    financingProgramMap,
    replaceDocumentLink,
    resetReplaceForm,
  ])

  const handleUnDiscard = useCallback(() => {
    if (!docsAutomation) return

    void setDiscardStatus({
      documentId: docsAutomation.id,
      versionTag: docsAutomation.versionTag,
      isDiscarded: false,
    }).then(() => {
      showSuccess()
    })
  }, [docsAutomation, setDiscardStatus, showSuccess])

  return (
    <Dialog open={Boolean(docsAutomation)} maxWidth="lg" onClose={handleClose}>
      <DialogTitle>
        <Stack gap={1}>
          <Informations docsAutomation={docsAutomation} />
          {errorLink && <ErrorAlert error={errorLink} />}
        </Stack>
      </DialogTitle>
      {isPending && <LinearProgress />}

      <DialogContent dividers>
        {!isDiscarded &&
          (!docsAutomation?.systemMatchingResult || docsAutomation.systemMatchingResult.length === 0) &&
          !docsAutomation?.linkedCreditApp && (
            <Typography textAlign="center" sx={{ mb: 2 }}>
              {t('common.noResults')}
            </Typography>
          )}
        {possibleMatchesResults}

        {!isDiscarded && multipleMatches && multipleMatches.length > 0 && (
          <Box sx={{ mt: 3, p: 2, border: 1, borderColor: 'warning.main', borderRadius: 1, bgcolor: 'warning.light' }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'warning.dark' }}>
              {t('docsAutomation.multipleMatchesFound')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('docsAutomation.multipleMatchesMessage')}
            </Typography>
            {multipleMatches.map((match) => (
              <Match
                key={match.id}
                possibleMatch={match}
                variant="outlined"
                onSelect={(m) => {
                  setSelectedMatch(m)
                  setMultipleMatches(null)
                  setShowManualLink(false)
                  resetManualLink()
                }}
                disabled={isPending}
                selected={selectedMatch?.id === match.id}
              />
            ))}
          </Box>
        )}

        {notes.length > 0 && (
          <Accordion
            expanded={expanded}
            onChange={handleExpandedChange}
            onTransitionEnd={handleScroll}
            disableGutters
            sx={{ border: 'none', boxShadow: 'none', '&::before': { display: 'none' } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={[
                { borderTop: 1, borderColor: 'divider' },
                expanded && {
                  position: 'sticky',
                  top: 0,
                  zIndex: 1000,
                  backgroundColor: 'background.paper',
                  borderTop: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                },
              ]}
            >
              <Typography>{t('docsAutomation.notes')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>{notes}</List>
            </AccordionDetails>
          </Accordion>
        )}

        {showManualLink && !isDiscarded && !isFinalized && (
          <ManualLinkForm
            control={manualLinkControl}
            numberType={numberType}
            numberTypeOptions={numberTypeOptions}
            financingTypeOptions={financingTypeOptions}
            isPending={isPending}
            onCancel={() => setShowManualLink(false)}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isDiscarded && (
            <Button
              variant="outlined"
              color="warning"
              onClick={handleUnDiscard}
              disabled={isPending || isUnDiscarding}
              loading={isUnDiscarding}
            >
              {t('docsAutomation.unDiscard')}
            </Button>
          )}
          {!showManualLink && !docsAutomation?.linkedCreditApp && !isDiscarded && !isFinalized && (
            <Button variant="outlined" color="primary" onClick={handleManualLinkClick} disabled={isPending}>
              {t('docsAutomation.manualLink')}
            </Button>
          )}
          {docsAutomation?.linkedCreditApp && !isFinalized && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleUnlinkClick}
              disabled={isPending || isUnlinking}
            >
              {t('docsAutomation.unlink')}
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} color="inherit" disabled={isPending || isUnlinking || isUnDiscarding}>
            {t('common.close')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={
              isPending ||
              isUnlinking ||
              isUnDiscarding ||
              isFinalized ||
              !!docsAutomation?.linkedCreditApp ||
              (!selectedMatch && !applicationNumber)
            }
            loading={isPending}
          >
            {t('common.save')}
          </Button>
        </Box>
      </DialogActions>

      <UnlinkConfirmDialog
        open={showUnlinkConfirm}
        onCancel={handleUnlinkCancel}
        onConfirm={handleUnlinkConfirm}
        isUnlinking={isUnlinking}
        isReplacing={isReplacing}
        wantsReplace={wantsReplace}
        onWantsReplaceChange={setWantsReplace}
        replaceFormControl={replaceFormControl}
        replaceNumberType={replaceNumberType}
        replaceApplicationNumber={replaceApplicationNumber}
        numberTypeOptions={numberTypeOptions}
      />
    </Dialog>
  )
}

export default MatchDialog
